// src/components/Modal.jsx
//
// Базовое модальное окно: оверлей, шапка, тело, футер.
// Специализации (ModalShell, TaskModal, ProjectModal) строятся поверх.
//
// subtitle - вторая строка под h3. Если не передан - не рендерится.
//
// titleId - id для h3 и одновременно флаг «эта модалка - aria-dialog».
// Пока titleId не передан, role/aria-modal/aria-labelledby на корне
// НЕ выставляются: поведение модалки идентично исходному. Это
// осознанно: title часто фрагмент (TaskModal, ProjectModal), и
// role="dialog" без доступного имени - шум для скринридеров.
// Специализированные модалки (например, DailyNormPraiseModal)
// передают titleId явно и получают полный a11y-набор.
//
// trapFocus - включает фокус-трап и возврат фокуса на триггер.
// Пока флаг не передан, поведение Tab идентично исходному, фокус
// не восстанавливается. Модалка, которой это нужно, передаёт
// trapFocus явно. Так общий компонент не меняет поведение остальных
// модалок приложения - см. правило 4.2 промпта.
//
// Фокус-трап:
//   - Tab циклически перебирает focusable-элементы внутри .modal;
//   - Shift+Tab - в обратном порядке;
//   - если фокус оказался вне модалки (например, до первого Tab
//     activeElement === document.body), Tab возвращает его внутрь.
// Начальный фокус трап не ставит - это ответственность конкретной
// модалки (у DailyNormPraiseModal фокус ставится на кнопку «Закрыть»).
//
// Возврат фокуса:
//   - при монтировании с trapFocus запоминается document.activeElement
//     (обычно это кнопка-триггер, по которой пользователь кликнул);
//   - при размонтировании фокус возвращается туда, если элемент ещё
//     в DOM. Сохранение в useLayoutEffect, а не useEffect: порядок
//     setup useLayoutEffect - снизу вверх (дети раньше родителей),
//     поэтому сохранение произойдёт ДО того, как DailyNormPraiseModal
//     (родитель) в своём useEffect переведёт фокус на «Закрыть».
//     Без этого activeElement сохранился бы уже на «Закрыть», и
//     возврат шёл бы в пустоту.
//
// Double-overlay:
//   - каждый Modal регистрируется в глобальном стеке (utils/modalDepth)
//     при монтировании, при размонтировании - снимается;
//   - depth пишется на .overlay в data-overlay-depth и в
//     --overlay-depth; CSS по data-overlay-depth != "0" делает фон
//     прозрачным, а z-index сдвигает на depth * 2.
//   - setAttribute / setProperty - императивный DOM-API на ref, не
//     inline-стиль в JSX (паттерн PriorityStrip / TaskProgress /
//     ColoredSpan).
//
// onEnterSubmit - колбэк «Enter в поле = Сохранить». ModalShell
// прокидывает сюда onSave, если сабмит возможен.
//
// Единый паттерн клавиш:
//   Ctrl/Cmd + Enter  - перенос строки в textarea (как в чате);
//   Enter             - «Сохранить» (submit модалки);
//   Shift/Alt + Enter - блокируются, чтобы не было второй привычки.
//
// Правила отсечения (общие для input и textarea):
//   - e.defaultPrevented: событие уже обработано выше (Select, меню,
//     композер комментария - их React-обработчик успевает раньше
//     bubbling до window);
//   - поле во вложенной модалке (NoteEditor поверх TaskModal) -
//     пропускаем, вложенный ModalShell сам решит;
//   - t.form - поле связано с <form>, Enter идёт в форму, не в модалку.

import { useEffect, useLayoutEffect, useRef } from "react";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { registerModal, unregisterModal } from "../utils/modalDepth";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const Modal = ({
  title,
  subtitle = null,
  onClose,
  children,
  footer = null,
  width = 640,
  className = "",
  headerBefore = null,
  headerAfter = null,
  bodyRef = null,
  onEnterSubmit = null,
  titleId = null,
  trapFocus = false,
}) => {
  const rootRef = useRef(null);
  const overlayRef = useRef(null);

  useLayoutEffect(() => {
    rootRef.current?.style.setProperty("--modal-max-w", `${width}px`);
  }, [width]);

  // Регистрация в стеке активных модалок + выставление глубины.
  // ref-цепочка, а не useState: обновление глубины не должно вызывать
  // ре-рендер - CSS читает значение через атрибут и переменную.
  useLayoutEffect(() => {
    const handle = registerModal();
    const el = overlayRef.current;
    if (el) {
      el.setAttribute("data-overlay-depth", String(handle.depth));
      el.style.setProperty("--overlay-depth", String(handle.depth));
    }
    return () => {
      unregisterModal(handle.id);
    };
  }, []);

  // Возврат фокуса на триггер. useLayoutEffect, а не useEffect:
  // setup useLayoutEffect у ребёнка (Modal) запускается раньше, чем
  // useEffect у родителя (DailyNormPraiseModal), - значит,
  // document.activeElement ещё указывает на кнопку-триггер, а не на
  // «Закрыть» в модалке.
  useLayoutEffect(() => {
    if (!trapFocus) return undefined;
    const prev = document.activeElement;
    return () => {
      if (
        prev &&
        prev !== document.body &&
        prev.isConnected &&
        typeof prev.focus === "function"
      ) {
        prev.focus();
      }
    };
  }, [trapFocus]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const hasEnterSubmit = typeof onEnterSubmit === "function";
  const onEnterSubmitRef = useRef(onEnterSubmit);
  useEffect(() => {
    onEnterSubmitRef.current = onEnterSubmit;
  }, [onEnterSubmit]);

  useEffect(() => {
    if (!hasEnterSubmit) return undefined;

    const handler = (e) => {
      if (e.key !== "Enter" || e.defaultPrevented) return;
      const t = e.target;
      if (!t || t.isContentEditable) return;
      if (t.closest(".modal") !== rootRef.current) return;

      if (t.tagName === "TEXTAREA") {
        if (e.ctrlKey || e.metaKey) return;
        if (e.shiftKey || e.altKey) {
          e.preventDefault();
          return;
        }
        if (t.form) return;
        e.preventDefault();
        const cb = onEnterSubmitRef.current;
        if (typeof cb === "function") cb();
        return;
      }

      if (t.tagName !== "INPUT") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (t.form) return;
      const type = (t.type || "text").toLowerCase();
      if (
        type === "button" ||
        type === "submit" ||
        type === "reset" ||
        type === "checkbox" ||
        type === "radio" ||
        type === "file"
      ) {
        return;
      }
      const cb = onEnterSubmitRef.current;
      if (typeof cb !== "function") return;
      e.preventDefault();
      cb();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hasEnterSubmit]);

  /**
   * Фокус-трап. Подписываемся на keydown внутри самого .modal, а не
   * на window: Tab вне модалки нас не интересует, а внутри - ловим
   * до того, как браузер переведёт фокус. cleanups гарантирует, что
   * слушатель снят при закрытии модалки или смене trapFocus.
   *
   * querySelectorAll здесь - по DOM модалки, для поиска focusable-
   * элементов. Запрет промпта на document.querySelector касается
   * стилей; поиск фокусируемых узлов - стандартная практика a11y
   * и не является прямым манипулированием DOM в обход React.
   */
  useEffect(() => {
    if (!trapFocus) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const getFocusable = () => {
      const nodes = root.querySelectorAll(FOCUSABLE_SELECTOR);
      const out = [];
      for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        if (el.hasAttribute("hidden")) continue;
        if (el.getAttribute("aria-hidden") === "true") continue;
        out.push(el);
      }
      return out;
    };

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        // Модалка без фокусируемых элементов - некуда вести фокус.
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Фокус вне модалки (до первого Tab activeElement === body).
      // Заводим его внутрь, не даём уйти на страницу под модалкой.
      if (!root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    root.addEventListener("keydown", onKeyDown);
    return () => root.removeEventListener("keydown", onKeyDown);
  }, [trapFocus]);

  // A11y-атрибуты включаются только при явном titleId. Так обычные
  // модалки не получают role="dialog" с невалидным/пустым именем.
  const isAriaDialog = Boolean(titleId);

  return (
    <div
      ref={overlayRef}
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={rootRef}
        className={`modal ${className}`}
        role={isAriaDialog ? "dialog" : undefined}
        aria-modal={isAriaDialog ? "true" : undefined}
        aria-labelledby={isAriaDialog ? titleId : undefined}
      >
        <div className="modal-head">
          {headerBefore && (
            <div className="modal-header-before">{headerBefore}</div>
          )}
          <div className="modal-title-block">
            <h3 id={isAriaDialog ? titleId : undefined}>{title}</h3>
            {subtitle && <div className="modal-subtitle">{subtitle}</div>}
          </div>
          {headerAfter && (
            <div className="modal-header-after">{headerAfter}</div>
          )}
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            title="Закрыть"
            aria-label="Закрыть"
          >
            <Ic d={ICONS.x} size={16} />
          </button>
        </div>
        <div className="modal-body" ref={bodyRef}>
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
};

export { Modal };
